import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../services/auth';
import { useModal } from '../../components/ModalProvider';
import { Panel, Badge, Button } from '../../components/design';

const CORE_API = '/api';

export default function ServerKeysSection({ serverId, server, canEdit }) {
  const { fetchWithAuth } = useAuth();
  const { confirm: showConfirm, alert: showAlert } = useModal();
  const queryClient = useQueryClient();

  const { data: keysData } = useQuery({
    queryKey: ['servers', serverId, 'ssh-keys'],
    queryFn: () => fetchWithAuth(`${CORE_API}/servers/${serverId}/ssh-keys`).then(r => r.json()),
    refetchInterval: 30000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'ssh-keys'] });
    queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'config'] });
    queryClient.invalidateQueries({ queryKey: ['ssh-keys'] });
  };

  const rotateMutation = useMutation({
    mutationFn: () => fetchWithAuth(`${CORE_API}/servers/${serverId}/ssh-key`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ remove_old: true }),
    }).then(async r => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Rotation failed');
      return data;
    }),
    onSuccess: () => { invalidate(); showAlert('SSH key rotated successfully', 'Success'); },
    onError: (err) => showAlert(err.message, 'Error'),
  });

  const syncMutation = useMutation({
    mutationFn: (keyId) => fetchWithAuth(`${CORE_API}/ssh-keys/${keyId}/sync`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }).then(async r => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Sync failed');
      return data;
    }),
    onSuccess: () => { invalidate(); showAlert('Key sync completed', 'Success'); },
    onError: (err) => showAlert(err.message, 'Error'),
  });

  const testMutation = useMutation({
    mutationFn: (keyId) => fetchWithAuth(`${CORE_API}/ssh-keys/${keyId}/test/${serverId}`, { method: 'POST' })
      .then(r => r.json()),
    onSuccess: (data) => showAlert(data.status === 'success' ? 'Key test succeeded' : `Key test failed: ${data.error || 'unknown'}`, data.status === 'success' ? 'Success' : 'Error'),
  });

  const unassignMutation = useMutation({
    mutationFn: (keyId) => fetchWithAuth(`${CORE_API}/ssh-keys/${keyId}/unassign`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ server_ids: [serverId] }),
    }).then(r => r.json()),
    onSuccess: () => { invalidate(); showAlert('Key unassigned from server', 'Success'); },
  });

  const handleRotate = async () => {
    if (!await showConfirm(
      'Rotate the SSH key for this server? A new key will be generated, pushed, verified, and the old synoti-managed key removed from the server.',
      'Rotate SSH Key'
    )) return;
    rotateMutation.mutate();
  };

  const handleUnassign = async (key) => {
    if (!await showConfirm(`Unassign key "${key.name}" from this server?`, 'Unassign Key')) return;
    unassignMutation.mutate(key.id);
  };

  const keys = keysData?.keys || [];

  return (
    <Panel title="SSH Keys" sub={`Primary key: ${server?.ssh_key_name || '—'}`} actions={
      <>
        {canEdit && (
          <Button variant="primary" size="sm" onClick={handleRotate} disabled={rotateMutation.isPending}>
            {rotateMutation.isPending ? 'Rotating...' : 'Rotate Key'}
          </Button>
        )}
        <a href="/ssh-keys" className="btn btn-ghost btn-sm">Manage Keys</a>
      </>
    }>
      {keys.length === 0 ? (
        <p className="muted" style={{ fontSize: 14 }}>No keys assigned to this server.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {keys.map(key => (
            <div key={key.id} className="flex items-center justify-between flex-wrap gap-2" style={{ padding: 12, borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>{key.name}</span>
                  <Badge tone="gray">{key.key_type}</Badge>
                  {server?.ssh_key_name === key.name && (
                    <Badge tone="amber">primary</Badge>
                  )}
                </div>
                <div className="mono muted" style={{ fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{key.fingerprint || 'fingerprint n/a'}</div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => syncMutation.mutate(key.id)} disabled={syncMutation.isPending}>Sync</Button>
                  <Button variant="ghost" size="sm" onClick={() => testMutation.mutate(key.id)} disabled={testMutation.isPending}>Test</Button>
                  <Button variant="danger" size="sm" onClick={() => handleUnassign(key)}>Unassign</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
